import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import Avatar from '../common/Avatar';
import CommentForm from './CommentForm';
import useAuth from '../../hooks/useAuth';
import { likeComment, unlikeComment, deleteComment } from '../../api/comments';

/**
 * CommentItem component for displaying individual comments
 * @param {Object} props - Component props
 * @param {Object} props.comment - Comment data
 * @param {string} props.postId - ID of the post the comment belongs to
 * @param {Function} props.onReplyAdded - Callback when a reply is added
 * @param {Function} props.onCommentDeleted - Callback when comment is deleted
 * @param {boolean} props.showReplyForm - Whether to show the reply form
 * @returns {JSX.Element} CommentItem component
 */
const CommentItem = ({
  comment,
  postId,
  onReplyAdded,
  onCommentDeleted,
  showReplyForm = false,
}) => {
  const { user, isAuth } = useAuth();
  const [isReplying, setIsReplying] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [likes, setLikes] = useState(comment.likes || []);
  const [likesCount, setLikesCount] = useState(comment.likesCount || 0);
  
  // Check if the current user has liked the comment
  const isLiked = user && likes.includes(user._id);
  
  // Check if the comment was made by the current user
  const isOwnComment = user && comment.user._id === user._id;
  
  // Format comment date
  const commentDate = new Date(comment.createdAt);
  const formattedDate = formatDistanceToNow(commentDate, { addSuffix: true });
  
  // Handle like/unlike
  const handleLikeToggle = async () => {
    if (!isAuth) return;
    
    try {
      setIsLiking(true);
      
      if (isLiked) {
        await unlikeComment(comment._id);
        setLikes(likes.filter((id) => id !== user._id));
        setLikesCount(likesCount - 1);
      } else {
        await likeComment(comment._id);
        setLikes([...likes, user._id]);
        setLikesCount(likesCount + 1);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      setIsLiking(false);
    }
  };
  
  // Handle comment deletion
  const handleDelete = async () => {
    if (!isAuth || !isOwnComment) return;
    
    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }
    
    try {
      setIsDeleting(true);
      await deleteComment(comment._id);
      
      if (onCommentDeleted) {
        onCommentDeleted(comment._id);
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Handle reply form submission
  const handleReplySubmit = (newReply) => {
    setIsReplying(false);
    
    if (onReplyAdded) {
      onReplyAdded(newReply);
    }
  };
  
  return (
    <div className="py-4">
      <div className="flex">
        {/* Avatar */}
        <div className="flex-shrink-0 mr-3">
          <Link to={`/profile/${comment.user.username}`}>
            <Avatar
              src={comment.user.profilePicture}
              alt={comment.user.name}
              size="sm"
              isOnline={comment.user.isOnline}
            />
          </Link>
        </div>
        
        {/* Comment content */}
        <div className="flex-1 min-w-0">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center">
                <Link
                  to={`/profile/${comment.user.username}`}
                  className="font-medium text-gray-900 hover:underline"
                >
                  {comment.user.name}
                </Link>
                <span className="mx-1 text-gray-500">·</span>
                <span className="text-gray-500 text-sm">
                  <time dateTime={comment.createdAt} title={commentDate.toLocaleString()}>
                    {formattedDate}
                  </time>
                </span>
                {comment.isEdited && (
                  <span className="ml-1 text-gray-500 text-xs">(edited)</span>
                )}
              </div>
              
              {/* Comment actions menu */}
              {isAuth && (
                <div className="relative">
                  <button
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Comment options"
                  >
                    <svg
                      className="h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
                      />
                    </svg>
                  </button>
                  
                  {/* Dropdown menu - implement popover functionality if needed */}
                </div>
              )}
            </div>
            
            {/* Comment text */}
            <div className="text-gray-800 whitespace-pre-wrap break-words">
              {comment.content}
            </div>
            
            {/* Comment media */}
            {comment.media && (
              <div className="mt-2">
                {comment.media.type === 'image' && (
                  <img
                    src={comment.media.url}
                    alt="Comment attachment"
                    className="max-h-60 rounded"
                  />
                )}
                {comment.media.type === 'video' && (
                  <video
                    src={comment.media.url}
                    controls
                    className="max-h-60 rounded"
                  />
                )}
              </div>
            )}
          </div>
          
          {/* Comment actions */}
          <div className="flex items-center mt-2 text-xs text-gray-500 space-x-4">
            {/* Like button */}
            <button
              className={`flex items-center ${
                isLiked ? 'text-blue-600' : 'hover:text-blue-600'
              }`}
              onClick={handleLikeToggle}
              disabled={isLiking || !isAuth}
            >
              <svg
                className="h-4 w-4 mr-1"
                xmlns="http://www.w3.org/2000/svg"
                fill={isLiked ? 'currentColor' : 'none'}
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"
                />
              </svg>
              <span>{likesCount}</span>
            </button>
            
            {/* Reply button */}
            <button
              className="flex items-center hover:text-blue-600"
              onClick={() => setIsReplying(!isReplying)}
              disabled={!isAuth}
            >
              <svg
                className="h-4 w-4 mr-1"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                />
              </svg>
              <span>Reply</span>
            </button>
            
            {/* Delete button (visible only for own comments) */}
            {isOwnComment && (
              <button
                className="flex items-center hover:text-red-600"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                <svg
                  className="h-4 w-4 mr-1"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
                <span>Delete</span>
              </button>
            )}
          </div>
          
          {/* Reply form */}
          {isReplying && (
            <div className="mt-3">
              <CommentForm
                postId={postId}
                parentId={comment._id}
                onSuccess={handleReplySubmit}
                onCancel={() => setIsReplying(false)}
                placeholder={`Reply to ${comment.user.name}...`}
              />
            </div>
          )}
          
          {/* Show/Hide replies button - only if the comment has replies */}
          {comment.replies && comment.replies.length > 0 && (
            <button
              className="mt-2 text-sm text-blue-600 hover:text-blue-800 flex items-center"
              onClick={() => setShowReplies(!showReplies)}
            >
              <svg
                className={`h-4 w-4 mr-1 transition-transform ${
                  showReplies ? 'rotate-90' : ''
                }`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
              {showReplies
                ? 'Hide replies'
                : `Show replies (${comment.replies.length})`}
            </button>
          )}
          
          {/* Replies */}
          {showReplies && comment.replies && comment.replies.length > 0 && (
            <div className="mt-2 ml-6 space-y-4">
              {comment.replies.map((reply) => (
                <CommentItem
                  key={reply._id}
                  comment={reply}
                  postId={postId}
                  onReplyAdded={onReplyAdded}
                  onCommentDeleted={onCommentDeleted}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

CommentItem.propTypes = {
  comment: PropTypes.object.isRequired,
  postId: PropTypes.string.isRequired,
  onReplyAdded: PropTypes.func,
  onCommentDeleted: PropTypes.func,
  showReplyForm: PropTypes.bool,
};

export default CommentItem;